import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, MailIcon, EditIcon, PrinterIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { Invoice } from '../types/Invoice';
export function InvoiceDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  // Mock data - in a real app, this would be fetched from an API
  const invoice: Invoice = {
    id: id || '1',
    invoiceNumber: 'INV-2024-001',
    customerName: 'Acme Corporation',
    customerEmail: 'billing@acme.com',
    customerPhone: '+94 77 123 4567',
    amount: 125000,
    currency: 'Rs.',
    status: 'paid',
    dueDate: '2024-01-15',
    issueDate: '2024-01-01',
    paidDate: '2024-01-10',
    description: 'Monthly subscription fee',
    notes: 'Thank you for your business!',
    items: [{
      id: '1',
      description: 'Premium Plan Subscription',
      quantity: 1,
      unitPrice: 100000,
      total: 100000
    }, {
      id: '2',
      description: 'Additional User Licenses',
      quantity: 5,
      unitPrice: 5000,
      total: 25000
    }],
    subtotal: 125000,
    taxAmount: 0,
    discountAmount: 0,
    total: 125000
  };
  const getStatusBadge = (status: Invoice['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles[status]}`} data-id="element-4074">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  return <div className="space-y-6" data-id="element-4075">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-4076">
        <button onClick={() => navigate('/invoices')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-4077">
          <ArrowLeftIcon size={20} data-id="element-4078" />
          Back to Invoices
        </button>
        <div className="flex items-center gap-3" data-id="element-4079">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4080">
            <PrinterIcon size={18} data-id="element-4081" />
            Print
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4082">
            <DownloadIcon size={18} data-id="element-4083" />
            Download PDF
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4084">
            <MailIcon size={18} data-id="element-4085" />
            Send Email
          </button>
          <button onClick={() => navigate(`/invoice-form?id=${invoice.id}`)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-4086">
            <EditIcon size={18} data-id="element-4087" />
            Edit
          </button>
        </div>
      </div>
      {/* Invoice Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-8" data-id="element-4088">
        {/* Invoice Header */}
        <div className="flex justify-between items-start mb-8" data-id="element-4089">
          <div data-id="element-4090">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-4091">INVOICE</h1>
            <p className="text-lg text-gray-600" data-id="element-4092">{invoice.invoiceNumber}</p>
          </div>
          <div className="text-right" data-id="element-4093">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/q86izFaWmRFfWc2CK9tiZz/logo.png" alt="DFCC Bank" className="h-12 mb-4" data-id="element-4094" />
            <p className="text-sm text-gray-600" data-id="element-4095">DFCC Bank</p>
            <p className="text-sm text-gray-600" data-id="element-4096">Colombo, Sri Lanka</p>
          </div>
        </div>
        {/* Status and Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 pb-8 border-b border-gray-200" data-id="element-4097">
          <div data-id="element-4098">
            <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-4099">Bill To:</h3>
            <p className="text-lg font-semibold text-gray-900" data-id="element-4100">
              {invoice.customerName}
            </p>
            <p className="text-sm text-gray-600" data-id="element-4101">{invoice.customerEmail}</p>
            {invoice.customerPhone && <p className="text-sm text-gray-600" data-id="element-4102">{invoice.customerPhone}</p>}
          </div>
          <div className="text-right" data-id="element-4103">
            <div className="mb-4" data-id="element-4104">{getStatusBadge(invoice.status)}</div>
            <div className="space-y-2" data-id="element-4105">
              <div className="flex justify-end gap-4" data-id="element-4106">
                <span className="text-sm text-gray-500" data-id="element-4107">Issue Date:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-4108">
                  {new Date(invoice.issueDate).toLocaleDateString('en-GB')}
                </span>
              </div>
              <div className="flex justify-end gap-4" data-id="element-4109">
                <span className="text-sm text-gray-500" data-id="element-4110">Due Date:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-4111">
                  {new Date(invoice.dueDate).toLocaleDateString('en-GB')}
                </span>
              </div>
              {invoice.paidDate && <div className="flex justify-end gap-4" data-id="element-4112">
                  <span className="text-sm text-gray-500" data-id="element-4113">Paid Date:</span>
                  <span className="text-sm font-medium text-green-600" data-id="element-4114">
                    {new Date(invoice.paidDate).toLocaleDateString('en-GB')}
                  </span>
                </div>}
            </div>
          </div>
        </div>
        {/* Invoice Items */}
        <div className="mb-8" data-id="element-4115">
          <table className="min-w-full" data-id="element-4116">
            <thead data-id="element-4117">
              <tr className="border-b border-gray-200" data-id="element-4118">
                <th className="py-3 text-left text-sm font-medium text-gray-500" data-id="element-4119">
                  Description
                </th>
                <th className="py-3 text-right text-sm font-medium text-gray-500" data-id="element-4120">
                  Quantity
                </th>
                <th className="py-3 text-right text-sm font-medium text-gray-500" data-id="element-4121">
                  Unit Price
                </th>
                <th className="py-3 text-right text-sm font-medium text-gray-500" data-id="element-4122">
                  Total
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4123">
              {invoice.items.map(item => <tr key={item.id} className="border-b border-gray-100" data-id="element-4124">
                  <td className="py-4 text-sm text-gray-900" data-id="element-4125">
                    {item.description}
                  </td>
                  <td className="py-4 text-sm text-gray-900 text-right" data-id="element-4126">
                    {item.quantity}
                  </td>
                  <td className="py-4 text-sm text-gray-900 text-right" data-id="element-4127">
                    Rs. {item.unitPrice.toLocaleString('en-LK')}
                  </td>
                  <td className="py-4 text-sm font-medium text-gray-900 text-right" data-id="element-4128">
                    Rs. {item.total.toLocaleString('en-LK')}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Totals */}
        <div className="flex justify-end mb-8" data-id="element-4129">
          <div className="w-full max-w-sm space-y-2" data-id="element-4130">
            <div className="flex justify-between py-2" data-id="element-4131">
              <span className="text-sm text-gray-600" data-id="element-4132">Subtotal:</span>
              <span className="text-sm font-medium text-gray-900" data-id="element-4133">
                Rs. {invoice.subtotal.toLocaleString('en-LK')}
              </span>
            </div>
            {invoice.discountAmount && invoice.discountAmount > 0 && <div className="flex justify-between py-2" data-id="element-4134">
                <span className="text-sm text-gray-600" data-id="element-4135">Discount:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-4136">
                  - Rs. {invoice.discountAmount.toLocaleString('en-LK')}
                </span>
              </div>}
            {invoice.taxAmount && invoice.taxAmount > 0 && <div className="flex justify-between py-2" data-id="element-4137">
                <span className="text-sm text-gray-600" data-id="element-4138">Tax:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-4139">
                  Rs. {invoice.taxAmount.toLocaleString('en-LK')}
                </span>
              </div>}
            <div className="flex justify-between py-3 border-t-2 border-gray-200" data-id="element-4140">
              <span className="text-lg font-semibold text-gray-900" data-id="element-4141">
                Total:
              </span>
              <span className="text-lg font-bold text-gray-900" data-id="element-4142">
                Rs. {invoice.total.toLocaleString('en-LK')}
              </span>
            </div>
          </div>
        </div>
        {/* Notes */}
        {invoice.notes && <div className="pt-6 border-t border-gray-200" data-id="element-4143">
            <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-4144">Notes:</h3>
            <p className="text-sm text-gray-600" data-id="element-4145">{invoice.notes}</p>
          </div>}
      </div>
      {/* Action Buttons */}
      {invoice.status === 'sent' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4146">
          <div className="flex items-center justify-between" data-id="element-4147">
            <div data-id="element-4148">
              <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-4149">
                Mark as Paid
              </h3>
              <p className="text-sm text-gray-600" data-id="element-4150">
                Confirm that this invoice has been paid
              </p>
            </div>
            <button className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700" data-id="element-4151">
              <CheckCircleIcon size={20} data-id="element-4152" />
              Mark as Paid
            </button>
          </div>
        </div>}
      {invoice.status !== 'cancelled' && invoice.status !== 'paid' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4153">
          <div className="flex items-center justify-between" data-id="element-4154">
            <div data-id="element-4155">
              <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-4156">
                Cancel Invoice
              </h3>
              <p className="text-sm text-gray-600" data-id="element-4157">
                This action cannot be undone
              </p>
            </div>
            <button className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700" data-id="element-4158">
              <XCircleIcon size={20} data-id="element-4159" />
              Cancel Invoice
            </button>
          </div>
        </div>}
    </div>;
}